import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../packages/WPMessageBanner/WPMessageBanner";
import WPTertiaryButton from "../../packages/WPTertiaryButton";

import { CorrectIcon, WrongIcon } from "../../assets/icons";

import "./TrainFileUsageTable.scss";

const TrainFileUsageTable = ({
  uploadedFiles = [],
  allTrainsStatus = [],
  plantFileId,
  showAppendColumn = false,
  setTempSelectedTrain,
}) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const { rowData, selectedFileName } = useMemo(() => {
    const targetFile = uploadedFiles.find(
      (file) => Number(file?.plant_file_id) === Number(plantFileId),
    );

    if (!targetFile) {
      return {
        rowData: [],
        selectedFileName: "",
      };
    }

    const statusByTrain = Object.fromEntries(
      allTrainsStatus
        .map((status) => [Number(status?.train), status])
        .filter(
          ([trainNumber]) => Number.isInteger(trainNumber) && trainNumber > 0,
        ),
    );

    const sheets = Array.isArray(targetFile.sheets) ? targetFile.sheets : [];
    const rowData = sheets
      .flatMap((sheet) => {
        return [...new Set((sheet?.train_nums || []).map(Number))].map(
          (trainNumber) => ({
            id: `${sheet?.sheet || "sheet"}-${trainNumber}`,
            trainNumber,
            sheetName: sheet?.sheet || "",
            columnMapping: Boolean(statusByTrain[trainNumber]?.column_mapping),
            referenceConditions: Boolean(
              statusByTrain[trainNumber]?.reference_conditions,
            ),
            defaultCipInformation: Boolean(
              statusByTrain[trainNumber]?.default_cip_information,
            ),
          }),
        );
      })
      .sort((first, second) => first.trainNumber - second.trainNumber);

    return {
      rowData,
      selectedFileName: targetFile.file_name || "",
    };
  }, [allTrainsStatus, uploadedFiles, plantFileId]);

  const bannerHeader = t("trainfileusagetable.fileUsedInOtherTrains", {
    fileName: selectedFileName,
  });

  const hintMessage = t(
    showAppendColumn
      ? "trainfileusagetable.replaceHint"
      : "trainfileusagetable.deleteHint",
  );

  const tableHeight = `${Math.min(200, Math.max(120, 60 + rowData.length * 40))}px`;

  const renderStatusIcon = (status) =>
    status ? (
      <CorrectIcon className="train-file-usage-table__status-icon" />
    ) : (
      <WrongIcon className="train-file-usage-table__status-icon" />
    );

  const columnDefs = [
    {
      headerName: t("trainfileusagetable.trainNumberHeader"),
      field: "trainNumber",
      cellRenderer: (params) => `${t("ValueStrings.train")} ${params.value}`,
      flex: 1,
    },
    {
      headerName: t("trainfileusagetable.sheetUsedHeader"),
      field: "sheetName",
      flex: 1,
    },
    {
      headerName: t("trainfileusagetable.columnMappingHeader"),
      field: "columnMapping",
      cellClass: "train-file-usage-table__status-cell",
      cellRenderer: (params) => renderStatusIcon(params.value),
      flex: 1,
    },
    {
      headerName: t("trainfileusagetable.referenceConditionsHeader"),
      field: "referenceConditions",
      cellClass: "train-file-usage-table__status-cell",
      cellRenderer: (params) => renderStatusIcon(params.value),
      flex: 1,
    },
    {
      headerName: t("trainfileusagetable.defaultCipHeader"),
      field: "defaultCipInformation",
      cellClass: "train-file-usage-table__status-cell",
      cellRenderer: (params) => renderStatusIcon(params.value),
      flex: 1,
    },
  ];

  if (showAppendColumn) {
    columnDefs.push({
      headerName: t("trainfileusagetable.appendDataHeader"),
      field: "appendAction",
      flex: 1,
      cellRenderer: (params) => (
        <WPTertiaryButton
          variant="text"
          size="small"
          label={t("trainfileusagetable.append")}
          className="train-file-usage-table__append-btn"
          onClick={() => handleAppendClick(params?.data?.trainNumber)}
        />
      ),
    });
  }

  const handleAppendClick = (appendClickedTrain) => {
    setTempSelectedTrain(appendClickedTrain);
  };

  if (!rowData.length) {
    return null;
  }

  return (
    <div className="train-file-usage-table">
      <WPMessageBanner
        type={BANNER_TYPES.error}
        header={bannerHeader}
        message={hintMessage}
        className="train-file-usage-table__banner"
      />

      <div className="ro-ag-table">
        <WPAgGrid
          gridRef={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          tableHeight={tableHeight}
        />
      </div>
    </div>
  );
};

export default TrainFileUsageTable;
